import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/* ═══════════════════════════════════════════════════════════
   LUXURY MOTION ENGINE
   Unified easing · heavy lerp dampening · clip-path reveals
   ═══════════════════════════════════════════════════════════ */

export const EASE = {
  luxury: "expo.out",
  liquid: "power3.out",
  float: "power1.inOut",
};

export const DURATION = {
  slow: 1.4,
  med: 0.9,
  fast: 0.5,
};

/* ── Device detection ─────────────────────────────────── */
export const isMobile = () =>
  window.matchMedia("(pointer: coarse)").matches ||
  window.innerWidth < 768;

export const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* ── Custom Cursor — heavy oil dampening ──────────────── */
export const initCursor = () => {
  if (isMobile() || prefersReducedMotion()) return () => {};

  const dot = document.querySelector(".lux-cursor");
  const ring = document.querySelector(".lux-cursor-ring");
  if (!dot || !ring) return () => {};

  let mouseX = window.innerWidth / 2;
  let mouseY = window.innerHeight / 2;
  let dotX = mouseX;
  let dotY = mouseY;
  let ringX = mouseX;
  let ringY = mouseY;

  const onMove = (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  };

  const onOver = (e) => {
    if (e.target.closest("a, button, .lux-card, input, textarea, [data-hover]")) {
      ring.classList.add("is-hovering");
    } else {
      ring.classList.remove("is-hovering");
    }
  };

  const tick = () => {
    // Heavy oil dampening — tight lerp
    dotX += (mouseX - dotX) * 0.35;
    dotY += (mouseY - dotY) * 0.35;
    ringX += (mouseX - ringX) * 0.12;
    ringY += (mouseY - ringY) * 0.12;

    dot.style.transform = `translate(${dotX - 4}px, ${dotY - 4}px)`;
    ring.style.transform = `translate(${ringX - 20}px, ${ringY - 20}px)`;

    requestAnimationFrame(tick);
  };

  window.addEventListener("mousemove", onMove, { passive: true });
  document.addEventListener("mouseover", onOver, { passive: true });
  tick();

  return () => {
    window.removeEventListener("mousemove", onMove);
    document.removeEventListener("mouseover", onOver);
  };
};

/* ── Cursor Parallax — elements float in heavy oil ───── */
export const initParallax = (scope = document) => {
  if (isMobile() || prefersReducedMotion()) return () => {};

  const targets = scope.querySelectorAll("[data-parallax]");
  if (!targets.length) return () => {};

  let mouseX = 0;
  let mouseY = 0;
  let rafId = null;

  const onMove = (e) => {
    mouseX = (e.clientX / window.innerWidth - 0.5) * 2;
    mouseY = (e.clientY / window.innerHeight - 0.5) * 2;
    if (!rafId) rafId = requestAnimationFrame(tick);
  };

  const tick = () => {
    targets.forEach((el) => {
      const depth = parseFloat(el.dataset.parallax) || 0.1;
      const x = mouseX * depth * 40;
      const y = mouseY * depth * 40;
      gsap.to(el, {
        x,
        y,
        duration: 1.2,
        ease: EASE.luxury,
        overwrite: "auto",
      });
    });
    rafId = null;
  };

  window.addEventListener("mousemove", onMove, { passive: true });

  return () => {
    window.removeEventListener("mousemove", onMove);
    if (rafId) cancelAnimationFrame(rafId);
  };
};

/* ── Clip-path line reveal for headings ──────────────── */
export const initLineReveals = (scope = document) => {
  if (prefersReducedMotion()) {
    scope.querySelectorAll(".reveal-line > span").forEach((el) => {
      gsap.set(el, { y: 0 });
    });
    return () => {};
  }

  const lines = scope.querySelectorAll(".reveal-line > span");
  if (!lines.length) return () => {};

  const triggers = [];

  lines.forEach((line) => {
    const trigger = gsap.to(line, {
      y: 0,
      duration: DURATION.slow,
      ease: EASE.luxury,
      scrollTrigger: {
        trigger: line.parentElement,
        start: "top 85%",
        toggleActions: "play none none reverse",
      },
    });
    triggers.push(trigger);
  });

  return () => {
    triggers.forEach((t) => t.scrollTrigger?.kill());
  };
};

/* ── Liquid fade reveal for images/cards ─────────────── */
export const initFadeReveals = (scope = document) => {
  if (prefersReducedMotion()) {
    scope.querySelectorAll(".reveal-fade").forEach((el) => {
      gsap.set(el, { opacity: 1, y: 0 });
    });
    return () => {};
  }

  const items = scope.querySelectorAll(".reveal-fade");
  if (!items.length) return () => {};

  const triggers = [];

  items.forEach((item, i) => {
    const trigger = gsap.to(item, {
      opacity: 1,
      y: 0,
      duration: DURATION.med,
      ease: EASE.liquid,
      delay: (i % 3) * 0.12,
      scrollTrigger: {
        trigger: item,
        start: "top 88%",
        toggleActions: "play none none reverse",
      },
    });
    triggers.push(trigger);
  });

  return () => {
    triggers.forEach((t) => t.scrollTrigger?.kill());
  };
};

/* ── Scroll parallax — elements drift at different speeds ─ */
export const initScrollParallax = (scope = document) => {
  if (isMobile() || prefersReducedMotion()) return () => {};

  const items = scope.querySelectorAll("[data-scroll-parallax]");
  if (!items.length) return () => {};

  const triggers = [];

  items.forEach((el) => {
    const speed = parseFloat(el.dataset.scrollParallax) || 0.2;
    const trigger = gsap.to(el, {
      y: () => speed * 200,
      ease: "none",
      scrollTrigger: {
        trigger: el,
        start: "top bottom",
        end: "bottom top",
        scrub: 1.2,
      },
    });
    triggers.push(trigger);
  });

  return () => {
    triggers.forEach((t) => t.scrollTrigger?.kill());
  };
};

/* ── Magnetic buttons ────────────────────────────────── */
export const initMagnetic = (scope = document) => {
  if (isMobile() || prefersReducedMotion()) return () => {};

  const buttons = scope.querySelectorAll("[data-magnetic]");
  if (!buttons.length) return () => {};

  const cleanups = [];

  buttons.forEach((btn) => {
    const strength = parseFloat(btn.dataset.magnetic) || 0.3;

    const onMove = (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      gsap.to(btn, {
        x: x * strength,
        y: y * strength,
        duration: 0.6,
        ease: EASE.luxury,
      });
    };

    const onLeave = () => {
      gsap.to(btn, {
        x: 0,
        y: 0,
        duration: 0.8,
        ease: EASE.luxury,
      });
    };

    btn.addEventListener("mousemove", onMove);
    btn.addEventListener("mouseleave", onLeave);

    cleanups.push(() => {
      btn.removeEventListener("mousemove", onMove);
      btn.removeEventListener("mouseleave", onLeave);
    });
  });

  return () => cleanups.forEach((fn) => fn());
};

/* ── Card spotlight (gold radial follows cursor) ─────── */
export const initCardSpotlight = (scope = document) => {
  if (isMobile()) return () => {};

  const cards = scope.querySelectorAll(".lux-card");
  if (!cards.length) return () => {};

  const cleanups = [];

  cards.forEach((card) => {
    const onMove = (e) => {
      const rect = card.getBoundingClientRect();
      card.style.setProperty("--mx", `${e.clientX - rect.left}px`);
      card.style.setProperty("--my", `${e.clientY - rect.top}px`);
    };
    card.addEventListener("mousemove", onMove);
    cleanups.push(() => card.removeEventListener("mousemove", onMove));
  });

  return () => cleanups.forEach((fn) => fn());
};

/* ── Count-up numbers ────────────────────────────────── */
export const initCountUp = (scope = document) => {
  const items = scope.querySelectorAll("[data-count]");
  if (!items.length) return () => {};

  const triggers = [];

  items.forEach((el) => {
    const end = parseFloat(el.dataset.count);
    const suffix = el.dataset.suffix || "";
    const duration = 2;

    const obj = { val: 0 };
    const trigger = gsap.to(obj, {
      val: end,
      duration,
      ease: EASE.luxury,
      scrollTrigger: {
        trigger: el,
        start: "top 85%",
        once: true,
      },
      onUpdate: () => {
        el.textContent = Math.round(obj.val) + suffix;
      },
    });
    triggers.push(trigger);
  });

  return () => {
    triggers.forEach((t) => t.scrollTrigger?.kill());
  };
};

/* ── Master init — run all on a scope ────────────────── */
export const initLuxuryMotion = (scope = document) => {
  const cleanups = [
    initCursor(),
    initParallax(scope),
    initLineReveals(scope),
    initFadeReveals(scope),
    initScrollParallax(scope),
    initMagnetic(scope),
    initCardSpotlight(scope),
    initCountUp(scope),
  ];

  return () => {
    cleanups.forEach((fn) => fn && fn());
    ScrollTrigger.refresh();
  };
};